import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Button,
  Stack,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import { ReceiptLong } from '@mui/icons-material';
import { billsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency, formatDate } from '../utils/helpers';
import LoadingButton from './LoadingButton';
import UnlockPdfDialog from './UnlockPdfDialog';

const DAY = 86400000;
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysUntil = (dueDate) => Math.round((Date.parse(dueDate) - Date.parse(todayIso())) / DAY);

const billName = (bill) =>
  bill.biller || [bill.institution, bill.last4 && `xx${bill.last4}`].filter(Boolean).join(' ') || 'Bill';

const isOpen = (bill) => ['open', 'part_paid', 'overdue'].includes(bill.status);

// "Due in 3 days" / "Due today" / "Overdue by 2 days" / "Paid"
function DueChip({ bill }) {
  if (!isOpen(bill)) {
    // Paid, though the amount due is in a locked PDF: the payment wasn't checked against it
    return bill.paymentLevel === 'unverified'
      ? <Tooltip title="A payment to this card was seen, but the amount due is in the statement PDF, so it couldn't be checked"><Chip size="small" color="success" variant="outlined" label="Paid (amount not checked)" /></Tooltip>
      : <Chip size="small" color="success" variant="outlined" label="Paid" />;
  }
  if (bill.paymentLevel === 'minimum' || bill.paymentLevel === 'partial') {
    return (
      <Tooltip title="Interest is charged on what's left after the due date">
        <Chip size="small" color="warning" label={bill.paymentLevel === 'minimum' ? 'Minimum paid' : 'Part paid'} />
      </Tooltip>
    );
  }
  if (!bill.dueDate) return <Chip size="small" variant="outlined" label="No due date" />;
  const days = daysUntil(bill.dueDate);
  if (days < 0) return <Chip size="small" color="error" label={`Overdue by ${-days} day${days === -1 ? '' : 's'}`} />;
  if (days === 0) return <Chip size="small" color="error" variant="outlined" label="Due today" />;
  return (
    <Chip
      size="small"
      color={days <= 5 ? 'warning' : 'default'}
      variant="outlined"
      label={`Due in ${days} day${days === 1 ? '' : 's'}`}
    />
  );
}

/**
 * Dashboard: card statements and other bills read from email. Unpaid bills
 * first (overdue at the top), then bills paid in the last 30 days.
 */
const BillsWidget = ({ sx }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [bills, setBills] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // The statement email whose locked PDF is being opened
  const [unlockEmailId, setUnlockEmailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await billsApi.getAll({ limit: 50 });
      setBills(response.data.bills || []);
    } catch (error) {
      setBills([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markPaid = async (bill) => {
    try {
      setSavingId(bill.id);
      await billsApi.update(bill.id, { markPaid: true });
      showSuccess(`${billName(bill)} marked as paid`);
      await load();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update the bill');
    } finally {
      setSavingId(null);
    }
  };

  if (!bills) return null;

  const today = todayIso();
  const monthAgo = new Date(Date.now() - 30 * DAY).toISOString().slice(0, 10);
  const open = bills
    .filter(isOpen)
    // Long-overdue bills were most likely paid in a way the app didn't see
    .filter(b => !b.dueDate || b.dueDate >= new Date(Date.now() - 45 * DAY).toISOString().slice(0, 10))
    .sort((a, b) => {
      const overdueA = a.dueDate && a.dueDate < today ? 0 : 1;
      const overdueB = b.dueDate && b.dueDate < today ? 0 : 1;
      return overdueA - overdueB || String(a.dueDate || '9999').localeCompare(String(b.dueDate || '9999'));
    });
  const recentlyPaid = bills
    .filter(b => b.status === 'paid' && (b.dueDate || b.statementDate || '') >= monthAgo)
    .slice(0, 3);
  const shown = [...open, ...recentlyPaid];

  if (shown.length === 0) return null;

  return (
    <Card sx={sx}>
      <CardHeader
        avatar={<ReceiptLong color="primary" />}
        title="Bills"
        subheader={open.length ? `${open.length} to pay` : 'All paid'}
        action={<Button size="small" onClick={() => navigate('/upcoming')}>View all</Button>}
      />
      <CardContent sx={{ pt: 0 }}>
        <List disablePadding>
          {shown.map((bill, index) => {
            const left = bill.totalDue === null ? null : Math.max(0, bill.totalDue - (bill.amountPaid || 0));
            return (
              <React.Fragment key={bill.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem disableGutters sx={{ alignItems: 'flex-start' }}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" fontWeight={500}>{billName(bill)}</Typography>
                        <DueChip bill={bill} />
                      </Stack>
                    }
                    secondary={
                      <>
                        {bill.dueDate ? `Due ${formatDate(bill.dueDate)}` : 'Due date not known'}
                        {bill.minimumDue !== null && isOpen(bill) ? ` · Minimum ${formatCurrency(bill.minimumDue)}` : ''}
                        {bill.status === 'part_paid' ? ` · ${formatCurrency(bill.amountPaid)} paid` : ''}
                      </>
                    }
                  />
                  <Box textAlign="right" sx={{ ml: 2, minWidth: 110 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={isOpen(bill) && bill.dueDate && bill.dueDate < today ? 'error.main' : 'text.primary'}
                    >
                      {left === null ? (bill.needsPdf ? 'In the PDF' : '—') : formatCurrency(isOpen(bill) ? left : bill.totalDue)}
                    </Typography>
                    {bill.needsPdf && bill.sourceEmailId && (
                      <Button size="small" onClick={() => setUnlockEmailId(bill.sourceEmailId)} sx={{ mt: 0.5, px: 1, minWidth: 0 }}>
                        Open PDF
                      </Button>
                    )}
                    {isOpen(bill) && (
                      <LoadingButton
                        size="small"
                        loading={savingId === bill.id}
                        onClick={() => markPaid(bill)}
                        sx={{ mt: 0.5, px: 1, minWidth: 0 }}
                      >
                        Mark paid
                      </LoadingButton>
                    )}
                  </Box>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </CardContent>
      <UnlockPdfDialog
        open={!!unlockEmailId}
        emailId={unlockEmailId}
        onClose={() => setUnlockEmailId(null)}
        onUnlocked={() => load()}
      />
    </Card>
  );
};

export default BillsWidget;
